import type { ComponentProps } from 'react';
import { useRouter } from 'next/router';
import useLocalStorage from '@rehooks/local-storage';
import { AnimatePresence, motion } from 'framer-motion';

import { cn } from '@poseidon/components/cn';
import { Kbd } from '@poseidon/components/Kbd';
import {
  Charts,
  Communication,
  Development,
  Files,
  General,
  Layout,
  Maps,
  Users,
} from '@poseidon/components/StrokeIcons';

import { useDeferAfterPaint } from '@/hooks/useDeferAfterPaint';
import { isMacLike, useKey } from '@/hooks/useKey';
import { api } from '@/utils/api';
import { useAuthContext } from '~/auth/auth.context';
import { LeftNavRow } from '~/components/LeftNav/LeftNavRow';
import { LogoRow } from '~/components/LeftNav/LogoRow';
import { UserLogoutRow } from '~/components/UserLogoutRow';

type UserTeamProps = ComponentProps<typeof UserLogoutRow>['authData'] & {
  teamId: string;
  userId: string;
  hubspotConnected?: string | null;
};

export const LeftNav = (props: { userTeam: UserTeamProps }) => {
  const router = useRouter();
  const [navMinimized, setNavMinimized] = useLocalStorage('navMinimized', false);

  // This only feeds a nav badge and is one of the slowest calls on the page, so
  // defer it past first paint to keep it off the critical load path.
  const deferred = useDeferAfterPaint();
  const hubspotCountsQuery = api.hubspot.getMatchesCount.useQuery(
    { teamId: props.userTeam.teamId },
    {
      enabled: deferred && props.userTeam.hubspotConnected != null && props.userTeam.hubspotConnected != 'off',
      // This is a relatively expensive count that barely changes between page
      // loads; keep it fresh for 5 min so navigation doesn't re-run it. HubSpot
      // mutations still invalidate it explicitly (useHubspotMutation).
      staleTime: 5 * 60 * 1000,
    },
  );
  const hubspotProblemCount = hubspotCountsQuery.data
    ? (hubspotCountsQuery.data.unsure_match ?? 0) +
      (hubspotCountsQuery.data.error ?? 0) +
      (hubspotCountsQuery.data.incomplete_contact ?? 0)
    : undefined;

  // Query AI Step count for Take Action badge
  const aiStepCountQuery = api.aiStep.countUnfinished.useQuery(
    {
      teamId: props.userTeam.teamId,
      userId: props.userTeam.userId,
    },
    { enabled: true, refetchInterval: 60000 }, // Refetch every minute
  );

  const takeActionCount = aiStepCountQuery.data?.count ?? 0;

  // Only while impersonating a teammate do we surface team-wide replies. Gated
  // here so regular sessions never see the badge (and never run the query).
  const [auth] = useAuthContext();
  const isImpersonating = !!auth.impersonation;
  const repliesQuery = api.contact.unhandledRepliesByPersona.useQuery(
    { teamId: props.userTeam.teamId },
    { enabled: isImpersonating, refetchInterval: 60000, staleTime: 60 * 1000 },
  );
  const repliesCount = repliesQuery.data?.total ?? 0;

  useKey('b', () => setNavMinimized(!navMinimized), { metaWindowsCtrlKey: true });
  useKey(
    'k',
    (e) => {
      // prevent default browser ctrl+k shortcut
      e.preventDefault();

      const page = '/prospects';
      if (router.pathname === page) {
        document.getElementById('search_input')?.focus();
      } else {
        void router.replace(page);
      }
    },
    { metaWindowsCtrlKey: true },
  );

  return (
    <AnimatePresence>
      <motion.div
        data-minimized={navMinimized}
        className={cn(
          'absolute inset-y-0 left-0 flex h-full w-(--nav-width) flex-col items-stretch border-r border-(--nav-border-color) bg-(--nav-bg-color) font-sans text-(--nav-txt-color) transition-[width] duration-200',
        )}
        transition={{ type: 'spring' }}>
        <LogoRow navMinimized={navMinimized} setNavMinimized={setNavMinimized} />

        <div
          data-intro="Things!"
          className={cn(
            'flex grow basis-0 flex-col items-stretch gap-1 overflow-auto px-4 transition-[padding] duration-300 ease-[cubic-bezier(0.05,0.61,0.41,0.95)] hide-scrollbar',
            navMinimized && 'px-[calc((var(--nav-width)-24px-16px)/2)]',
          )}>
          <LeftNavRow
            href="/todo"
            icon={<General.Zap />}
            minimized={navMinimized}
            counterValue={takeActionCount > 0 ? takeActionCount : undefined}
            gradientBadge={takeActionCount > 0}
            gradientActive={takeActionCount > 0}>
            Take Action
          </LeftNavRow>
          {isImpersonating && (
            <LeftNavRow
              href={{ pathname: '/leads/responses', query: { userId: 'team' } }}
              icon={<Communication.MessageCheckSquare />}
              minimized={navMinimized}
              counterValue={repliesCount > 0 ? repliesCount : undefined}
              gradientBadge={repliesCount > 0}
              gradientActive={repliesCount > 0}>
              Replies
            </LeftNavRow>
          )}
          <LeftNavRow
            href="/templates"
            icon={<Maps.Rocket />}
            minimized={navMinimized}
            addLink="/templates/add"
            addLinkLabel="Add Campaign">
            Campaigns
          </LeftNavRow>
          <LeftNavRow
            href="/prompts"
            icon={<Communication.MessageTextSquare02 />}
            minimized={navMinimized}
            addLink="/prompts/add"
            addLinkLabel="Add Message">
            Templates
          </LeftNavRow>

          <LeftNavRow href="/prospects" icon={<Users.Users01 />} minimized={navMinimized}>
            Search <Kbd>{isMacLike() ? '⌘' : 'ctrl'}+K</Kbd>
          </LeftNavRow>
          <LeftNavRow href="/pipeline" icon={<Layout.AlignTopArrow02 />} minimized={navMinimized}>
            Pipeline
          </LeftNavRow>

          <LeftNavRow
            href="/results"
            icon={<Users.UsersPlus />}
            minimized={navMinimized}
            subLinks={[
              {
                href: '/activities',
                icon: <Charts.BarChartSquare02 />,
                children: 'Analytics',
              },
            ]}>
            Connection results
          </LeftNavRow>
          <LeftNavRow
            href="/content"
            icon={<Files.FileAttachment02 />}
            addLink="/content?add=true"
            addLinkLabel="Add Content"
            minimized={navMinimized}>
            Content Library
          </LeftNavRow>

          {props.userTeam.hubspotConnected != null && props.userTeam.hubspotConnected != 'off' && (
            <LeftNavRow
              href="/hubspot/unmatched"
              counterValue={hubspotProblemCount}
              icon={<Development.GitMerge />}
              minimized={navMinimized}>
              CRM Sync
            </LeftNavRow>
          )}
          <div className="min-h-10" />
          <LeftNavRow href="/uploadcsv" icon={<General.UploadCloud02 />} minimized={navMinimized}>
            Upload CSV
          </LeftNavRow>
        </div>
        <div className="mx-4 mb-8 shrink-0 border-t border-(--nav-border-color) bg-(--nav-bg-color)">
          <UserLogoutRow authData={props.userTeam} minimized={navMinimized} />
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
